import { useCallback, useEffect, useState } from "react";
import { fmtMYT, fmtMYTDate } from "@/lib/myt";
import { LineChart, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PurchaseModal, fmtCoins } from "@/components/shop/PurchaseModal";

const STATE_STYLES = {
  available: "text-[#94A3B8] border-[#222530]",
  pending: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  awaiting_details: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  active: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  expired: "text-red-400 bg-red-400/10 border-red-400/30",
  rejected: "text-red-400 bg-red-400/10 border-red-400/30",
  renewal_pending: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  locked: "text-[#94A3B8] border-[#222530] bg-[#222530]/40",
  paused: "text-amber-400 bg-amber-400/10 border-amber-400/30",
};
const STATE_LABELS = {
  available: "Available",
  pending: "Pending",
  awaiting_details: "Action Required",
  active: "Active",
  expired: "Expired",
  rejected: "Rejected",
  renewal_pending: "Renewal Pending",
  locked: "Locked",
  paused: "Paused",
};

function AccessBadge({ ind }) {
  if (ind.access_type === "coin") {
    return <span className="text-[9px] font-mono tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-2 py-0.5" data-testid={`member-indicator-access-${ind.name}`}>Coins</span>;
  }
  return <span className="text-[9px] font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/30 rounded-full px-2 py-0.5" data-testid={`member-indicator-access-${ind.name}`}>Free</span>;
}

function IndicatorImage({ indicator, className }) {
  if (indicator.image_url) {
    return <img loading="lazy" src={indicator.image_url} alt={indicator.name} className={`${className} object-cover`} />;
  }
  return (
    <div className={`${className} bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center`}>
      <LineChart className="h-5 w-5 text-[#D4AF37]" />
    </div>
  );
}

export default function MemberIndicators() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null); // indicator object
  const [form, setForm] = useState({ email: "", registered_broker: "", tradingview_username: "" });
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [balanceMinor, setBalanceMinor] = useState(0);
  const [buyTarget, setBuyTarget] = useState(null);

  const load = useCallback(() => {
    api.get("/member/indicator-library").then((res) => setData(res.data)).catch(() => setData({ indicators: [] }));
    api.get("/member/shop/purchases").then((res) => setPurchases(res.data.purchases || [])).catch(() => setPurchases([]));
    api.get("/member/coins").then((res) => setBalanceMinor(res.data.balance_minor || 0)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const openDetail = (indicator, freshData) => {
    const d = freshData || data;
    setFormError("");
    setForm({
      email: d?.defaults?.email || "",
      registered_broker: d?.defaults?.registered_broker || "",
      tradingview_username: "",
    });
    setDetail(indicator);
  };

  const afterPurchase = async (target) => {
    // Purchase is paid and exactly-once; now collect TradingView delivery details
    // (no second charge — the claim endpoint only records delivery details).
    setBuyTarget(null);
    try {
      const res = await api.get("/member/indicator-library");
      setData(res.data);
      api.get("/member/shop/purchases").then((r) => setPurchases(r.data.purchases || [])).catch(() => {});
      api.get("/member/coins").then((r) => setBalanceMinor(r.data.balance_minor || 0)).catch(() => {});
      toast.success("Purchase confirmed — submit your TradingView details to activate.");
      const item = (res.data.indicators || []).find((i) => i.id === target?.id);
      if (item && item.state === "awaiting_details") openDetail(item, res.data);
    } catch {
      load();
    }
  };

  const submitClaim = async (e) => {
    e.preventDefault();
    setFormError("");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/member/indicators/${detail.id}/claim`, form);
      toast.success(res.message);
      setDetail(null);
      load();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const renew = async (indicator) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/member/indicators/${indicator.id}/renew`);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const indicators = data?.indicators;
  const ownedPurchase = {};
  const expiredPurchase = {};
  purchases.forEach((p) => {
    if (p.product_type !== "indicator") return;
    if (p.status === "active") ownedPurchase[p.product_id] = p;
    else if (p.status === "expired" && !expiredPurchase[p.product_id]) expiredPurchase[p.product_id] = p;
  });

  return (
    <div className="space-y-8" data-testid="member-indicators-page">
      <div>
        <p className="eyebrow-label mb-2">Indicator Library</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">My Indicators</h1>
        <p className="mt-2 text-sm text-[#64748B]">
          Premium TradingView indicators published by PLUSMAKER administration. Claim your first indicator below.
        </p>
      </div>

      {indicators === null || indicators === undefined ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 animate-pulse" data-testid="member-indicators-loading">
          {[...Array(3)].map((_, i) => <div key={i} className="card-lux h-56" />)}
        </div>
      ) : indicators.length === 0 ? (
        <div className="card-lux p-10 text-center" data-testid="member-indicators-empty">
          <LineChart className="h-8 w-8 text-[#D4AF37] mx-auto mb-4" />
          <p className="text-sm text-[#94A3B8] max-w-md mx-auto leading-relaxed">
            No indicators published yet. New indicators will appear here as administration releases them.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {indicators.map((ind) => (
            <div key={ind.id} className="card-lux card-lux-hover p-6 flex flex-col" data-testid={`member-indicator-card-${ind.name}`}>
              <div className="flex items-start justify-between mb-4">
                <IndicatorImage indicator={ind} className="h-12 w-12 rounded-lg" />
                <div className="flex flex-col items-end gap-1.5">
                  <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${STATE_STYLES[ind.state]}`} data-testid={`member-indicator-state-${ind.name}`}>
                    {ind.state === "locked" && ind.access_type === "coin" ? "Coins Required" : STATE_LABELS[ind.state]}
                  </span>
                  <AccessBadge ind={ind} />
                </div>
              </div>
              <h3 className="font-heading text-lg font-semibold text-white">{ind.name}</h3>
              {ind.coin_price_minor != null && (
                <p className="mt-1 text-xs font-mono text-[#D4AF37]" data-testid={`member-indicator-price-${ind.name}`}>
                  {fmtCoins(ind.coin_price_minor)} · {ind.coin_duration_days ? `${ind.coin_duration_days} days access` : "Permanent access"}
                </p>
              )}
              <p className="mt-2 text-xs text-[#94A3B8] leading-relaxed flex-1">{ind.description}</p>
              {ind.state === "active" && (
                <p className="mt-3 text-xs font-mono text-emerald-400" data-testid={`member-indicator-expiry-${ind.name}`}>
                  {ind.coin_owned && ownedPurchase[ind.id]
                    ? ownedPurchase[ind.id].expires_at
                      ? `Owned — valid until ${fmtMYTDate(ownedPurchase[ind.id].expires_at)}`
                      : "Owned — Permanent access"
                    : `Valid Until: ${ind.expires_at ? fmtMYTDate(ind.expires_at) : "—"}`}
                </p>
              )}
              {!ind.coin_owned && expiredPurchase[ind.id] && (
                <p className="mt-3 text-xs font-mono text-red-400" data-testid={`member-indicator-coin-expired-${ind.name}`}>
                  Expired on {fmtMYTDate(expiredPurchase[ind.id].expires_at)} — repurchase to regain access.
                </p>
              )}
              {ind.state === "active" && ind.tutorial_link && (
                <a href={ind.tutorial_link} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:text-[#E5C158] underline underline-offset-4 transition-colors" data-testid={`member-indicator-tutorial-${ind.name}`}>
                  Watch Tutorial
                </a>
              )}
              {(ind.state === "pending" || ind.state === "awaiting_details") && ind.state_note && (
                <p className="mt-3 text-[11px] text-[#D4AF37]" data-testid={`member-indicator-note-${ind.name}`}>{ind.state_note}</p>
              )}
              {ind.state === "renewal_pending" && (
                <p className="mt-3 text-[11px] text-[#D4AF37]">Your renewal request is under administration review.</p>
              )}
              {ind.state === "rejected" && ind.state_note && (
                <p className="mt-3 text-[11px] text-red-400">Reason: {ind.state_note}</p>
              )}
              {ind.state === "locked" && (
                <p className="mt-3 text-[11px] text-[#D4AF37] flex items-center gap-1.5" data-testid={`member-indicator-locknote-${ind.name}`}>
                  <Lock className="h-3 w-3 shrink-0" /> {ind.state_note}
                </p>
              )}
              {ind.state === "paused" && (
                <p className="mt-3 text-[11px] text-amber-400" data-testid={`member-indicator-pausenote-${ind.name}`}>{ind.state_note}</p>
              )}
              <div className="gold-divider my-4" />
              {ind.coin_price_minor != null && !ind.coin_owned && !["active", "awaiting_details", "pending"].includes(ind.state) && (
                <button
                  onClick={() => setBuyTarget({ id: ind.id, name: ind.name, product_type: "indicator", price_minor: ind.coin_price_minor, term_label: ind.coin_duration_days ? `${ind.coin_duration_days}-day access (no auto-renewal)` : "Permanent access" })}
                  className="btn-gold w-full py-2.5 text-xs mb-2"
                  data-testid={`member-indicator-buy-${ind.name}`}
                >
                  {expiredPurchase[ind.id] ? "Repurchase" : "Buy"} — {fmtCoins(ind.coin_price_minor)}
                </button>
              )}
              {ind.state === "awaiting_details" && (
                <button onClick={() => openDetail(ind)} className="btn-gold w-full py-2.5 text-xs" data-testid={`member-indicator-submit-details-${ind.name}`}>
                  Submit TradingView Details
                </button>
              )}
              {ind.coin_owned && ind.state === "active" && (
                <p className="text-center text-[10px] font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/30 rounded-full px-2 py-1 mb-2" data-testid={`member-indicator-owned-${ind.name}`}>Owned</p>
              )}
              {(ind.state === "available" || ind.state === "rejected") && (
                <button onClick={() => openDetail(ind)} className="btn-gold w-full py-2.5 text-xs" data-testid={`member-indicator-claim-${ind.name}`}>
                  {ind.state === "rejected" ? "Resubmit Request" : "Claim Now"}
                </button>
              )}
              {ind.state === "expired" && ind.claim_mode === "renew" && (
                <button onClick={() => renew(ind)} disabled={busy} className="btn-gold w-full py-2.5 text-xs flex items-center justify-center gap-2 disabled:opacity-60" data-testid={`member-indicator-renew-${ind.name}`}>
                  <RefreshCw className="h-3.5 w-3.5" /> Renew Access
                </button>
              )}
              {(ind.state === "active" || ind.state === "pending" || ind.state === "renewal_pending") && (
                <button onClick={() => openDetail(ind)} className="btn-outline-gold w-full py-2.5 text-xs" data-testid={`member-indicator-view-${ind.name}`}>
                  View Details
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {buyTarget && (
        <PurchaseModal
          product={buyTarget}
          balanceMinor={balanceMinor}
          onClose={() => setBuyTarget(null)}
          onPurchased={() => afterPurchase(buyTarget)}
        />
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="indicator-detail-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDetail(null)} />
          <div className="card-lux relative w-full max-w-lg p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start gap-4 mb-5">
              <IndicatorImage indicator={detail} className="h-14 w-14 rounded-xl shrink-0" />
              <div>
                <p className="eyebrow-label mb-1">Indicator Details</p>
                <h3 className="font-heading text-xl font-semibold text-white" data-testid="indicator-detail-name">{detail.name}</h3>
              </div>
            </div>
            <p className="text-sm text-[#94A3B8] leading-relaxed mb-6" data-testid="indicator-detail-description">
              {detail.full_description || detail.description}
            </p>

            {(["first", "ib"].includes(detail.claim_mode) || detail.state === "awaiting_details") && (
              <form onSubmit={submitClaim} className="space-y-4" data-testid="indicator-claim-form">
                {detail.state === "awaiting_details" && (
                  <p className="text-[11px] text-[#D4AF37] border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg px-4 py-3" data-testid="claim-purchase-note">
                    Purchase confirmed ({fmtCoins(detail.coin_price_minor)} paid) — submit your delivery details below. Your indicator activates after administration approval. No additional charge.
                  </p>
                )}
                {detail.claim_mode === "ib" && (
                  <p className="text-[11px] text-[#D4AF37] border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] rounded-lg px-4 py-3" data-testid="claim-second-note">
                    You already hold an active indicator — additional indicators are reviewed by your IB{data?.ib?.name ? ` (${data.ib.name})` : ""} first, then administration. Submit your delivery details below.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3 text-xs border border-[#222530] rounded-lg px-4 py-3">
                  <div>
                    <p className="text-[#64748B]">IB Name</p>
                    <p className="text-white" data-testid="claim-ib-name">{data?.ib?.name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[#64748B]">IB UID</p>
                    <p className="font-mono text-[#D4AF37]" data-testid="claim-ib-uid">{data?.ib?.uid || "—"}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Email Address <span className="text-red-400">*</span></label>
                  <input type="email" className="input-lux" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required data-testid="claim-email-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Registered Broker <span className="text-red-400">*</span></label>
                  <input className="input-lux font-mono uppercase" value={form.registered_broker} onChange={(e) => setForm((f) => ({ ...f, registered_broker: e.target.value.toUpperCase() }))} placeholder="VERSUS TRADE" required data-testid="claim-broker-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">TradingView Username <span className="text-red-400">*</span></label>
                  <input className="input-lux font-mono" value={form.tradingview_username} onChange={(e) => setForm((f) => ({ ...f, tradingview_username: e.target.value }))} required data-testid="claim-tradingview-input" />
                </div>
                {formError && (
                  <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="claim-form-error">{formError}</p>
                )}
                <button type="submit" disabled={busy} className="btn-gold w-full py-3 text-sm disabled:opacity-60" data-testid="claim-submit-button">
                  {busy ? "Submitting..." : detail.state === "awaiting_details" ? "Submit Delivery Details" : detail.claim_mode === "ib" ? "Submit via My IB" : "Claim Indicator"}
                </button>
              </form>
            )}

            {detail.claim_mode === "renew" && (
              <div className="text-center">
                <p className="text-sm text-[#94A3B8]">This access expired on {detail.expires_at ? fmtMYTDate(detail.expires_at) : "—"}.</p>
                <button onClick={() => { renew(detail); setDetail(null); }} disabled={busy} className="btn-gold w-full py-3 text-sm mt-5 disabled:opacity-60" data-testid="renew-submit-button">
                  {busy ? "Submitting..." : "Renew Access"}
                </button>
              </div>
            )}

            {detail.state === "active" && (
              <p className="text-sm text-emerald-400 text-center border border-emerald-400/25 bg-emerald-400/[0.06] rounded-lg px-4 py-3" data-testid="indicator-detail-validity">
                {detail.expires_at
                  ? `Access active — valid until ${fmtMYTDate(detail.expires_at)}.`
                  : "Access active — Permanent access."}
              </p>
            )}

            {detail.state === "pending" && detail.state_note && (
              <p className="text-sm text-[#D4AF37] text-center border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] rounded-lg px-4 py-3" data-testid="indicator-detail-pending-note">{detail.state_note}</p>
            )}

            <button onClick={() => setDetail(null)} className="btn-outline-gold w-full py-2.5 text-sm mt-5" data-testid="indicator-detail-close-button">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
